import React, { useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import Button from "../../../components/button/Button";
import Input from "../../../components/form-item/Input";
import Toggle from "../../../components/form-item/Toggle";
import NumberStepper from "../../../components/form-item/NumberStepper";
import Avatar from "../../../components/avatar/Avatar";
import { useSearchAdminProjectsQuery } from "../../../app/services/adminAcr";
import {
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useGetRoleCandidatesQuery,
  type NotePosition,
  type NoteSection,
  type NoteUserBrief,
  type SectionRoleInput,
  type SectionRoleName,
} from "../../../app/services/notes";

/**
 * セクションの作成・権限設定モーダル。
 *
 * - 基本設定（外部公開 / 対象プロジェクト / 閲覧ポジション / アクセス期間）:
 *   master_admin（canManage）のみ
 * - ロール（オーナー / メンテナー / コントリビューター）と保護ルール
 *   （オーナー承認必須 / 必要承認人数）: master_admin とセクションオーナー
 *
 * 外部公開（is_public）はトグルで切り替え、ON のあいだは閲覧制限として
 * 機能しない「対象プロジェクト / 閲覧できるポジション / アクセス期間」を
 * 非表示にする（マイサイトの公開設定と同じパターン）。state と送信値は
 * 保持したままなので、OFF に戻すと元の設定が復元される。
 *
 * 対象プロジェクトは非公開セクションでは 1 件以上必須（公開セクションのみ
 * 未選択を許容。ヘルプセンター等）。ロールはポジション紐付け（対象
 * プロジェクトの該当ポジション全員）とユーザー直接指定の両方で割り当て
 * られるため、プロジェクト未紐付けの公開セクションは管理者のみ編集できる。
 */

const POSITION_OPTIONS: { value: NotePosition; label: string }[] = [
  { value: "CLIENT", label: "クライアント" },
  { value: "REVIEWER", label: "レビュアー" },
  { value: "PRODUCER", label: "プロデューサー" },
  { value: "DIRECTOR", label: "ディレクター" },
  { value: "SPECIALIST", label: "スペシャリスト" },
];

const ROLE_DEFS: {
  role: SectionRoleName;
  label: string;
  hint: string;
}[] = [
  {
    role: "owner",
    label: "オーナー",
    hint: "ロール・保護ルールの変更とレビュー/マージができます",
  },
  {
    role: "maintainer",
    label: "メンテナー",
    hint: "レビュー/マージと削除・並べ替えができます",
  },
  {
    role: "contributor",
    label: "コントリビューター",
    hint: "プルリクエスト（変更提案）を作成できます",
  },
];

const FieldLabel = styled.p.attrs({
  className: "heading-13 u-line-height-150",
})`
  margin: 0;
`;

const FieldHint = styled.p.attrs({
  className: "bodytext-11 u-text-light-gray u-line-height-150",
})`
  margin: 0;
`;

const CheckRowButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 2px 0;
  cursor: pointer;
  text-align: left;
`;

const CheckRowIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $checked: boolean }>`
  font-size: 24px;
  color: ${(props) =>
    props.$checked ? "var(--soremo-blue)" : "var(--soremo-border)"};
`;

/** Material Symbols アイコンのチェックボックス行（タスクボード等の既存デザインに準拠） */
const CheckRow: React.FC<{
  checked: boolean;
  onToggle: () => void;
  label: string;
}> = ({ checked, onToggle, label }) => (
  <CheckRowButton
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
  >
    <CheckRowIcon $checked={checked}>
      {checked ? "check_box" : "check_box_outline_blank"}
    </CheckRowIcon>
    <span className="bodytext-13">{label}</span>
  </CheckRowButton>
);

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #eaf6fb;
  color: var(--soremo-blue);

  button {
    display: flex;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: inherit;

    .material-symbols-rounded {
      font-size: 14px;
    }
  }
`;

const SearchResultButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;

  .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }

  @media (hover: hover) {
    &:hover {
      background: #f7f7f7;
    }
  }
`;

/* ロールごとの割り当てブロック（枠付きボックスは utility に該当なし） */
const RoleBox = styled.div.attrs({ className: "u-col u-gap8" })`
  align-items: stretch;
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fcfcfc;
`;

interface SelectedProject {
  id: string;
  name: string;
}

/** ロールごとの割り当て状態 */
interface RoleAssignments {
  positions: NotePosition[];
  users: NoteUserBrief[];
}

type RolesState = Record<SectionRoleName, RoleAssignments>;

const emptyRolesState = (): RolesState => ({
  owner: { positions: [], users: [] },
  maintainer: { positions: [], users: [] },
  contributor: { positions: [], users: [] },
});

export interface SectionSettingsModalProps {
  /** null なら新規作成 */
  section: NoteSection | null;
  /** master_admin（全設定の編集可）。false ならロール/保護ルールのみ */
  canManage: boolean;
  onClose: () => void;
}

const SectionSettingsModal: React.FC<SectionSettingsModalProps> = ({
  section,
  canManage,
  onClose,
}) => {
  const isNew = section === null;
  const settings = section?.settings;

  const [title, setTitle] = useState(section?.title ?? "");
  const [isPublic, setIsPublic] = useState(settings?.is_public ?? false);
  const [selectedProjects, setSelectedProjects] = useState<SelectedProject[]>(
    settings?.projects ?? [],
  );
  const [allowedPositions, setAllowedPositions] = useState<NotePosition[]>(
    settings?.allowed_positions ?? [],
  );
  const [allowCompleted, setAllowCompleted] = useState(
    settings?.allow_completed ?? true,
  );
  // マイページ「ヘルプセンター」への割当（外部公開セクションのみ）
  const [helpCenterClient, setHelpCenterClient] = useState(
    settings?.help_center_client ?? false,
  );
  const [helpCenterCreator, setHelpCenterCreator] = useState(
    settings?.help_center_creator ?? false,
  );
  const [roles, setRoles] = useState<RolesState>(() => {
    const state = emptyRolesState();
    for (const entry of settings?.roles ?? []) {
      if (entry.position) {
        state[entry.role].positions.push(entry.position);
      } else if (entry.user) {
        state[entry.role].users.push(entry.user);
      }
    }
    return state;
  });
  const [requireOwnerApproval, setRequireOwnerApproval] = useState(
    settings?.require_owner_approval ?? false,
  );
  const [requiredApprovals, setRequiredApprovals] = useState(
    settings?.required_approvals ?? 1,
  );
  const [projectQuery, setProjectQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userQueryRole, setUserQueryRole] = useState<SectionRoleName | null>(
    null,
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [createSection, { isLoading: creating }] = useCreateSectionMutation();
  const [updateSection, { isLoading: updating }] = useUpdateSectionMutation();
  const [deleteSection, { isLoading: deleting }] = useDeleteSectionMutation();

  const { data: searchData, isFetching: searching } =
    useSearchAdminProjectsQuery(
      { q: projectQuery, limit: 8 },
      { skip: !canManage || projectQuery.trim() === "" },
    );

  const searchResults = useMemo(() => {
    const selectedIds = new Set(selectedProjects.map((p) => p.id));
    return (searchData?.data?.projects ?? []).filter(
      (p) => !selectedIds.has(p.project_id),
    );
  }, [searchData, selectedProjects]);

  // ロールへのユーザー直接指定の検索（既存セクションのみ。
  // 新規作成時は保存後に設定できる）
  const { data: candidateData, isFetching: searchingUsers } =
    useGetRoleCandidatesQuery(
      { section_id: section?.id ?? "", q: userQuery },
      { skip: isNew || !userQueryRole || userQuery.trim() === "" },
    );

  const candidates = useMemo(() => {
    if (!userQueryRole) return [];
    const assignedIds = new Set(roles[userQueryRole].users.map((u) => u.id));
    return (candidateData?.data?.users ?? []).filter(
      (u) => !assignedIds.has(u.id),
    );
  }, [candidateData, roles, userQueryRole]);

  const togglePosition = (
    list: NotePosition[],
    setList: (next: NotePosition[]) => void,
    position: NotePosition,
  ) => {
    setList(
      list.includes(position)
        ? list.filter((p) => p !== position)
        : [...list, position],
    );
  };

  const toggleRolePosition = (role: SectionRoleName, position: NotePosition) =>
    setRoles((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        positions: prev[role].positions.includes(position)
          ? prev[role].positions.filter((p) => p !== position)
          : [...prev[role].positions, position],
      },
    }));

  const addRoleUser = (role: SectionRoleName, user: NoteUserBrief) => {
    setRoles((prev) => ({
      ...prev,
      [role]: { ...prev[role], users: [...prev[role].users, user] },
    }));
    setUserQuery("");
  };

  const removeRoleUser = (role: SectionRoleName, userId: number) =>
    setRoles((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        users: prev[role].users.filter((u) => u.id !== userId),
      },
    }));

  const buildRolesInput = (): SectionRoleInput[] => {
    const result: SectionRoleInput[] = [];
    for (const { role } of ROLE_DEFS) {
      for (const position of roles[role].positions) {
        result.push({ role, position });
      }
      for (const user of roles[role].users) {
        result.push({ role, user_id: user.id });
      }
    }
    return result;
  };

  const hasOwner =
    roles.owner.positions.length > 0 || roles.owner.users.length > 0;

  // 非公開セクションはいずれかのプロジェクトへの紐付けが必須
  // （公開セクションのみ未選択を許容。BE でも同じ検証をする）。
  const projectsMissing =
    canManage && !isPublic && selectedProjects.length === 0;

  const handleSave = async () => {
    if (canManage && !title.trim()) {
      toast.error("セクション名を入力してください");
      return;
    }
    if (projectsMissing) {
      toast.error("プロジェクトを 1 つ以上選択してください");
      return;
    }
    if (requireOwnerApproval && !hasOwner) {
      toast.error("オーナー承認を必須にするにはオーナーを設定してください");
      return;
    }
    const rolePayload = {
      roles: buildRolesInput(),
      require_owner_approval: requireOwnerApproval,
      required_approvals: requiredApprovals,
    };
    try {
      // ヘルプセンター割当は外部公開セクションのみ有効
      // （公開を OFF にして保存した場合は割当も解除する）。
      const helpCenterPayload = {
        help_center_client: isPublic && helpCenterClient,
        help_center_creator: isPublic && helpCenterCreator,
      };
      if (isNew) {
        await createSection({
          title: title.trim(),
          is_public: isPublic,
          project_ids: selectedProjects.map((p) => p.id),
          allowed_positions: allowedPositions,
          allow_completed: allowCompleted,
          ...helpCenterPayload,
          ...rolePayload,
        }).unwrap();
        toast.success("セクションを作成しました");
      } else if (canManage) {
        await updateSection({
          section_id: section.id,
          title: title.trim(),
          is_public: isPublic,
          project_ids: selectedProjects.map((p) => p.id),
          allowed_positions: allowedPositions,
          allow_completed: allowCompleted,
          ...helpCenterPayload,
          ...rolePayload,
        }).unwrap();
        toast.success("セクション設定を保存しました");
      } else {
        // セクションオーナーはロール・保護ルールのみ変更できる
        await updateSection({
          section_id: section.id,
          ...rolePayload,
        }).unwrap();
        toast.success("ロール設定を保存しました");
      }
      onClose();
    } catch {
      toast.error("保存に失敗しました");
    }
  };

  const handleDelete = async () => {
    if (!section) return;
    try {
      await deleteSection({ section_id: section.id }).unwrap();
      toast.success("セクションを削除しました");
      onClose();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  if (confirmingDelete && section) {
    return (
      <ConfirmModal
        message={`セクション「${section.title}」を削除しますか？\n配下のノートも表示されなくなります。`}
        confirmLabel="削除"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    );
  }

  return (
    <Modal
      title={isNew ? "セクションを追加" : "セクション設定"}
      titleSize="medium"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: isNew ? "作成" : "保存",
        onClick: handleSave,
        loading: creating || updating,
        disabled: projectsMissing,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      <div className="u-col u-gap16">
        {canManage && (
          <>
            <Input
              label="セクション名"
              option="required"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 利用マニュアル"
              maxlength={255}
            />

            <div className="u-col u-gap4">
              <FieldLabel>外部公開</FieldLabel>
              <Toggle value={isPublic} onClick={() => setIsPublic((v) => !v)}>
                <span className="bodytext-13">
                  {isPublic
                    ? "ログインなしで誰でも閲覧できます"
                    : "対象プロジェクトのメンバーのみ閲覧できます"}
                </span>
              </Toggle>
              {isPublic && (
                <FieldHint>
                  ヘルプセンター等での利用を想定しています。誰でも閲覧できる
                  ため、対象プロジェクト・閲覧ポジション・アクセス期間の設定
                  は非表示になります（編集できるメンバーは「ロール」で設定）。
                </FieldHint>
              )}
            </div>

            {isPublic && (
              <div className="u-col u-gap4">
                <FieldLabel>ヘルプセンターに割当</FieldLabel>
                <FieldHint>
                  マイページのメニュー「ヘルプセンター」の遷移先として
                  このセクションを使います（各ロールにつき 1 セクションのみ。
                  他のセクションに割当済みの場合は自動で付け替えます）。
                </FieldHint>
                <CheckRow
                  checked={helpCenterClient}
                  onToggle={() => setHelpCenterClient((v) => !v)}
                  label="クライアント向け"
                />
                <CheckRow
                  checked={helpCenterCreator}
                  onToggle={() => setHelpCenterCreator((v) => !v)}
                  label="クリエイター向け"
                />
              </div>
            )}

            {!isPublic && (
              <>
                <div className="u-col u-gap8">
                  <FieldLabel>対象プロジェクト</FieldLabel>
                  <div className="u-col u-gap8">
                    {selectedProjects.length > 0 && (
                      <div
                        className="u-row u-gap4"
                        style={{ flexWrap: "wrap" }}
                      >
                        {selectedProjects.map((p) => (
                          <Chip key={p.id} className="bodytext-11">
                            {p.name}
                            <button
                              aria-label={`${p.name} を外す`}
                              onClick={() =>
                                setSelectedProjects((prev) =>
                                  prev.filter((sp) => sp.id !== p.id),
                                )
                              }
                            >
                              <span className="material-symbols-rounded">
                                close
                              </span>
                            </button>
                          </Chip>
                        ))}
                      </div>
                    )}
                    <Input
                      type="search"
                      value={projectQuery}
                      onChange={(e) => setProjectQuery(e.target.value)}
                      placeholder="プロジェクト名で検索..."
                    />
                    {searching && <FieldHint>検索中...</FieldHint>}
                    {searchResults.map((p) => (
                      <SearchResultButton
                        key={p.project_id}
                        onClick={() => {
                          setSelectedProjects((prev) => [
                            ...prev,
                            { id: p.project_id, name: p.name },
                          ]);
                          setProjectQuery("");
                        }}
                      >
                        <span className="material-symbols-rounded">add</span>
                        <span className="bodytext-13">{p.name}</span>
                      </SearchResultButton>
                    ))}
                    {selectedProjects.length === 0 &&
                      projectQuery.trim() === "" && (
                        <FieldHint>
                          プロジェクトを 1 つ以上選択してください
                        </FieldHint>
                      )}
                  </div>
                </div>

                <div className="u-col u-gap4">
                  <FieldLabel>閲覧できるポジション</FieldLabel>
                  <FieldHint>
                    ポジションが未選択の場合は対象プロジェクトの全メンバーが
                    閲覧できます
                  </FieldHint>
                  {POSITION_OPTIONS.map((opt) => (
                    <CheckRow
                      key={`view-${opt.value}`}
                      checked={allowedPositions.includes(opt.value)}
                      onToggle={() =>
                        togglePosition(
                          allowedPositions,
                          setAllowedPositions,
                          opt.value,
                        )
                      }
                      label={opt.label}
                    />
                  ))}
                </div>

                <div className="u-col u-gap4">
                  <FieldLabel>アクセス期間</FieldLabel>
                  <Toggle
                    value={allowCompleted}
                    onClick={() => setAllowCompleted((v) => !v)}
                  >
                    <span className="bodytext-13">
                      {allowCompleted
                        ? "プロジェクト完了後もアクセス可"
                        : "プロジェクト進行中のみアクセス可"}
                    </span>
                  </Toggle>
                </div>
              </>
            )}
          </>
        )}

        <div className="u-col u-gap8">
          <FieldLabel>ロール</FieldLabel>
          <FieldHint>
            公開データの更新はプルリクエスト経由で行われます。ポジション
            紐付けとユーザー直接指定のどちらでも割り当てられます。
            未設定の場合は管理者のみ編集できます。
          </FieldHint>
          {isPublic && selectedProjects.length === 0 && (
            <FieldHint>
              プロジェクトに紐づいていない外部公開セクションのため、
              ポジション紐付けは使えません。編集者を追加するには、
              ユーザー検索（全ユーザーが対象）で直接指定してください
            </FieldHint>
          )}
          {ROLE_DEFS.map(({ role, label, hint }) => (
            <RoleBox key={role}>
              <div className="u-col u-gap4">
                <span className="heading-13 u-line-height-150">{label}</span>
                <FieldHint>{hint}</FieldHint>
              </div>
              <div className="u-row u-gap4" style={{ flexWrap: "wrap" }}>
                {POSITION_OPTIONS.map((opt) => (
                  <CheckRow
                    key={`${role}-${opt.value}`}
                    checked={roles[role].positions.includes(opt.value)}
                    onToggle={() => toggleRolePosition(role, opt.value)}
                    label={opt.label}
                  />
                ))}
              </div>
              {roles[role].users.length > 0 && (
                <div className="u-row u-gap4" style={{ flexWrap: "wrap" }}>
                  {roles[role].users.map((user) => (
                    <Chip key={user.id} className="bodytext-11">
                      {user.display_name}
                      <button
                        aria-label={`${user.display_name} を外す`}
                        onClick={() => removeRoleUser(role, user.id)}
                      >
                        <span className="material-symbols-rounded">close</span>
                      </button>
                    </Chip>
                  ))}
                </div>
              )}
              {isNew ? (
                <FieldHint>
                  ユーザー直接指定はセクション作成後に設定できます
                </FieldHint>
              ) : (
                <>
                  <Input
                    type="search"
                    value={userQueryRole === role ? userQuery : ""}
                    onChange={(e) => {
                      setUserQueryRole(role);
                      setUserQuery(e.target.value);
                    }}
                    placeholder="表示名 / ユーザー名 / メールで検索して直接指定..."
                  />
                  {userQueryRole === role && searchingUsers && (
                    <FieldHint>検索中...</FieldHint>
                  )}
                  {userQueryRole === role &&
                    candidates.map((user) => (
                      <SearchResultButton
                        key={user.id}
                        onClick={() => addRoleUser(role, user)}
                      >
                        <Avatar size={32} src={user.avatar ?? undefined} />
                        <span className="bodytext-13">{user.display_name}</span>
                        {user.positions.length > 0 && (
                          <span className="bodytext-11 u-text-light-gray">
                            {user.positions.join(" / ")}
                          </span>
                        )}
                      </SearchResultButton>
                    ))}
                </>
              )}
            </RoleBox>
          ))}
        </div>

        <div className="u-col u-gap8">
          <FieldLabel>保護ルール</FieldLabel>
          <Toggle
            value={requireOwnerApproval}
            onClick={() => setRequireOwnerApproval((v) => !v)}
          >
            <span className="bodytext-13">オーナーの承認を必須にする</span>
            <span className="bodytext-11 u-text-light-gray u-ml8">
              マージにはオーナーの承認が必要になります
            </span>
          </Toggle>
          {requireOwnerApproval && !hasOwner && (
            <FieldHint>
              オーナーが未設定です。オーナーを設定してください。
            </FieldHint>
          )}
          <div className="u-row u-gap8 u-items-center">
            <span className="bodytext-13">必要承認人数</span>
            <NumberStepper
              value={requiredApprovals}
              min={1}
              max={10}
              onChange={setRequiredApprovals}
              aria-label="必要承認人数"
            />
            <FieldHint>
              起票者以外の承認可能者が足りない場合はその人数まで自動で緩和されます
            </FieldHint>
          </div>
        </div>

        {!isNew && canManage && (
          <div className="u-row-end">
            <Button
              size="small"
              theme="secondary"
              variant="text"
              leftIcon="delete"
              onClick={() => setConfirmingDelete(true)}
            >
              セクションを削除
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SectionSettingsModal;
